import {
  collection, query, orderBy, onSnapshot, doc, updateDoc,
  Timestamp, Unsubscribe, where,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import apiClient from '../../services/apiClient';
import type { InboxMessage, UserInfo, SendMessageRequest, ReplyRequest, NotifyThread } from './models';

/* ═══════════════════ Firestore (real-time) ═══════════════════ */

/** Subscribe to inbox messages (real-time). Returns unsubscribe function. */
export function subscribeInbox(
  userId: string,
  onData: (msgs: InboxMessage[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'inbox', userId, 'messages'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, snap => {
    const msgs: InboxMessage[] = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
    } as InboxMessage));
    // Sort by updatedAt (falls back to createdAt for old messages without updatedAt)
    msgs.sort((a, b) => {
      const ta = (a.updatedAt || a.createdAt)?.toMillis?.() ?? 0;
      const tb = (b.updatedAt || b.createdAt)?.toMillis?.() ?? 0;
      return tb - ta;
    });
    onData(msgs);
  }, onError);
}

/** Subscribe to sent messages (real-time). */
export function subscribeSent(
  userId: string,
  onData: (msgs: InboxMessage[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'inbox', userId, 'sent'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, snap => {
    const msgs: InboxMessage[] = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
    } as InboxMessage));
    onData(msgs);
  }, onError);
}

/** Subscribe to unread count (real-time). */
export function subscribeUnreadCount(
  userId: string,
  onCount: (count: number) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'inbox', userId, 'messages'),
    where('isRead', '==', false),
  );
  return onSnapshot(q, snap => {
    onCount(snap.size);
  });
}

/** Mark a message as read in Firestore. */
export async function markAsRead(userId: string, messageId: string): Promise<void> {
  const ref = doc(db, 'inbox', userId, 'messages', messageId);
  await updateDoc(ref, {
    isRead: true,
    readAt: Timestamp.now(),
  });
}

/* ═══════════════════ Backend API ═══════════════════ */

/** Send a message via backend (writes to Firestore). */
export async function sendMessage(data: SendMessageRequest): Promise<string> {
  const res = await apiClient.post('/api/v1/inbox/send', data);
  return res.data.data.id;
}

/** Reply to an existing message (appends to conversation). */
export async function replyToMessage(data: ReplyRequest): Promise<void> {
  await apiClient.post('/api/v1/inbox/reply', data);
}

/** Hide a message (removes from inbox list until a new reply arrives). */
export async function hideMessage(messageId: string): Promise<void> {
  await apiClient.put(`/api/v1/inbox/messages/${messageId}/hide`);
}

/** Unhide a message (restore to inbox list). */
export async function unhideMessage(messageId: string): Promise<void> {
  await apiClient.put(`/api/v1/inbox/messages/${messageId}/hide?hidden=false`);
}

/** Upload an image for inbox message. */
export async function uploadImage(file: File): Promise<{ url: string; fileId: string; fileName: string; fileSize: number }> {
  const formData = new FormData();
  formData.append('image', file);
  const res = await apiClient.post('/api/v1/inbox/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

/** Upload a general file for inbox message (max 10MB). */
export async function uploadFile(file: File): Promise<{ url: string; fileId: string; fileName: string; fileSize: number }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiClient.post('/api/v1/inbox/upload-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

/** Fetch active users for recipient picker. */
export async function fetchUsers(): Promise<UserInfo[]> {
  const res = await apiClient.get('/api/v1/inbox/users');
  return res.data.data || [];
}

/* ═══════════════════ Helpers ═══════════════════ */

/** Extract image file from clipboard paste event. */
export function extractClipboardImage(e: ClipboardEvent): File | null {
  const items = e.clipboardData?.items;
  if (!items) return null;
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf('image') !== -1) {
      return items[i].getAsFile();
    }
  }
  return null;
}

/** Format Firestore timestamp for display. */
export function formatTimestamp(ts: Timestamp | null | undefined): string {
  if (!ts) return '';
  const d = ts.toDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Format ISO date string for display. */
export function formatDateStr(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ═══════════════════ Order Notifications (PostgreSQL API) ═══════════════════ */

export async function fetchNotifyThreads(limit = 20, offset = 0): Promise<{ threads: NotifyThread[]; total: number }> {
  const res = await apiClient.get('/api/v1/notifications/threads', { params: { limit, offset } });
  return res.data.data;
}

export async function fetchNotifyDetail(id: number): Promise<NotifyThread> {
  const res = await apiClient.get(`/api/v1/notifications/threads/${id}`);
  return res.data.data;
}

export async function markNotifyRead(id: number): Promise<void> {
  await apiClient.put(`/api/v1/notifications/threads/${id}/read`);
}

export async function fetchNotifyUnreadCount(): Promise<number> {
  const res = await apiClient.get('/api/v1/notifications/unread-count');
  return res.data.data || 0;
}
